import { useEffect, useState, useMemo } from 'react'
import { salesApi, sellersApi } from '../../services/api'
import ExportBar, { ExportColumn } from '../../components/ExportBar'

const ROWS_PER_PAGE = 100

const SALES_EXPORT_COLUMNS: ExportColumn<any>[] = [
  { key: 'id', label: 'ID', format: (s) => `#${s.id}` },
  { key: 'sale_date', label: 'Fecha', format: (s) => new Date(s.sale_date).toLocaleDateString() },
  { key: 'seller_name', label: 'Vendedor', format: (s) => s.seller_name || '-' },
  { key: 'variant_name', label: 'Variante', format: (s) => s.variant_name || '-' },
  { key: 'quantity', label: 'Cantidad', format: (s) => String(s.quantity) },
  { key: 'unit_price', label: 'Precio Unitario', format: (s) => `$${Number(s.unit_price || 0).toFixed(2)}` },
  { key: 'total_amount', label: 'Total', format: (s) => `$${Number(s.total_amount || 0).toFixed(2)}` },
  { key: 'customer_name', label: 'Cliente', format: (s) => s.customer_name || '-' },
  { key: 'customer_payment_mode', label: 'Modo', format: (s) => s.customer_payment_mode || '-' },
  { key: 'customer_district', label: 'Distrito', format: (s) => s.customer_district || '-' },
]

export default function SalesPage() {
  const [sales, setSales] = useState<any[]>([])
  const [sellers, setSellers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filterYear, setFilterYear] = useState('')
  const [filterMonth, setFilterMonth] = useState('')
  const [filterDay, setFilterDay] = useState('')
  const [filterCustomer, setFilterCustomer] = useState('')
  const [filterDistrict, setFilterDistrict] = useState('')
  const [filterSellerId, setFilterSellerId] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    fetchSales()
    sellersApi.list().then((res) => setSellers(res.data.sellers || [])).catch(() => {})
  }, [])

  const fetchSales = async () => {
    try {
      const response = await salesApi.list()
      setSales(response.data.sales || [])
    } catch (error) {
      console.error('Error fetching sales:', error)
    } finally {
      setLoading(false)
    }
  }

  const years = useMemo(() => {
    const set = new Set<string>()
    sales.forEach((s) => {
      if (s.sale_date) set.add(new Date(s.sale_date).getFullYear().toString())
    })
    return Array.from(set).sort().reverse()
  }, [sales])

  const filtered = useMemo(() => {
    return sales.filter((s) => {
      if (!s.sale_date) return false
      const d = new Date(s.sale_date)
      if (filterYear && d.getFullYear().toString() !== filterYear) return false
      if (filterMonth && (d.getMonth() + 1).toString().padStart(2, '0') !== filterMonth) return false
      if (filterDay && d.getDate().toString().padStart(2, '0') !== filterDay) return false
      if (filterCustomer) {
        const q = filterCustomer.toLowerCase()
        if (!(s.customer_name || '').toLowerCase().includes(q)) return false
      }
      if (filterDistrict) {
        const q = filterDistrict.toLowerCase()
        if (!(s.customer_district || '').toLowerCase().includes(q)) return false
      }
      if (filterSellerId && String(s.seller_id) !== filterSellerId) return false
      return true
    })
  }, [sales, filterYear, filterMonth, filterDay, filterCustomer, filterDistrict, filterSellerId])

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE)
  const paged = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE
    return filtered.slice(start, start + ROWS_PER_PAGE)
  }, [filtered, page])

  const clearFilters = () => {
    setFilterYear('')
    setFilterMonth('')
    setFilterDay('')
    setFilterCustomer('')
    setFilterDistrict('')
    setFilterSellerId('')
    setPage(1)
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-espresso-800 mb-6">Gestión de Ventas</h1>

      {/* Filters */}
      <div className="bg-cream-50 shadow rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-espresso-700">Filtros</h3>
          <button onClick={clearFilters} className="text-xs text-terracotta-700 hover:text-terracotta-800">
            Limpiar todo
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs font-medium text-espresso-500 mb-1">Vendedor</label>
            <select value={filterSellerId} onChange={(e) => { setFilterSellerId(e.target.value); setPage(1) }}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
              <option value="">Todos</option>
              {sellers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.full_name}{s.status !== 'active' ? ' (inactivo)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-espresso-500 mb-1">Año</label>
            <select value={filterYear} onChange={(e) => { setFilterYear(e.target.value); setPage(1) }}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
              <option value="">Todos</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-espresso-500 mb-1">Mes</label>
            <select value={filterMonth} onChange={(e) => { setFilterMonth(e.target.value); setPage(1) }}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
              <option value="">Todos</option>
              {Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0')).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-espresso-500 mb-1">Día</label>
            <select value={filterDay} onChange={(e) => { setFilterDay(e.target.value); setPage(1) }}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
              <option value="">Todos</option>
              {Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, '0')).map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-espresso-500 mb-1">Cliente</label>
            <input type="text" value={filterCustomer} placeholder="Buscar..."
              onChange={(e) => { setFilterCustomer(e.target.value); setPage(1) }}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-espresso-500 mb-1">Distrito</label>
            <input type="text" value={filterDistrict} placeholder="Buscar..."
              onChange={(e) => { setFilterDistrict(e.target.value); setPage(1) }}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
          </div>
        </div>
      </div>

      {/* Results info + Pagination */}
      <div className="flex items-center justify-between mb-4 text-sm text-espresso-600">
        <span>{filtered.length} venta{filtered.length !== 1 ? 's' : ''} encontrada{filtered.length !== 1 ? 's' : ''}</span>
        {totalPages > 1 && (
          <div className="flex items-center space-x-2">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              className="px-3 py-1 border border-espresso-300 rounded-md text-sm hover:bg-cream-50 disabled:opacity-50">
              Anterior
            </button>
            <span>Página {page} de {totalPages}</span>
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
              className="px-3 py-1 border border-espresso-300 rounded-md text-sm hover:bg-cream-50 disabled:opacity-50">
              Siguiente
            </button>
          </div>
        )}
      </div>

      <ExportBar data={filtered} columns={SALES_EXPORT_COLUMNS} filename="ventas" title="Gestión de Ventas" />

      {/* Table */}
      <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-espresso-100">
          <thead className="bg-cream-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Fecha</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Vendedor</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Variante</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Cantidad</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Precio Unitario</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Total</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Cliente</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Modo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Distrito</th>
            </tr>
          </thead>
          <tbody className="bg-cream-50 divide-y divide-espresso-100">
            {paged.map((sale) => (
              <tr key={sale.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-800 font-medium">#{sale.id}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-800">
                  {new Date(sale.sale_date).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-800">{sale.seller_name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-800">{sale.variant_name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-800">{sale.quantity}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-800">${Number(sale.unit_price || 0).toFixed(2)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">${Number(sale.total_amount || 0).toFixed(2)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-500">{sale.customer_name || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {sale.customer_payment_mode ? (
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      sale.customer_payment_mode === 'credit'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {sale.customer_payment_mode}
                    </span>
                  ) : (
                    <span className="text-espresso-400 text-sm">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-500">{sale.customer_district || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {paged.length === 0 && (
          <div className="text-center py-8 text-espresso-500">No hay ventas que coincidan con los filtros.</div>
        )}
      </div>

      {/* Bottom pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center mt-4 space-x-2 text-sm">
          <button onClick={() => setPage(1)} disabled={page === 1}
            className="px-3 py-1 border border-espresso-300 rounded-md hover:bg-cream-50 disabled:opacity-50">
            Primero
          </button>
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
            className="px-3 py-1 border border-espresso-300 rounded-md hover:bg-cream-50 disabled:opacity-50">
            Anterior
          </button>
          <span className="text-espresso-600">Página {page} de {totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
            className="px-3 py-1 border border-espresso-300 rounded-md hover:bg-cream-50 disabled:opacity-50">
            Siguiente
          </button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
            className="px-3 py-1 border border-espresso-300 rounded-md hover:bg-cream-50 disabled:opacity-50">
            Último
          </button>
        </div>
      )}
    </div>
  )
}
